import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { buildDateTree, filterDateTree, nodeState, toggleNode, type DateTree } from "../lib/dateTree";
import { SORT_LABELS, type SortDir, type SortKind } from "../lib/ledgerView";

const MAX_OPTIONS = 500;

// The in-header column filter: a trigger button (▾ / ● when active) plus a searchable
// checklist popover of the column's distinct values. The popover is rendered through a
// PORTAL to document.body and positioned fixed from the trigger's rect, so it escapes
// BOTH the grid's overflow clipping AND the stacking contexts that sticky <th> cells
// create (which otherwise paint sibling headers on top of an in-cell popover). It
// closes on outside-mousedown and on any outside scroll/resize (a fixed popover would
// otherwise drift away from its header).
// `dateColumn` swaps the flat checklist for a Year > Month > Day tree (lib/dateTree.ts), the
// way Excel filters a real date column; values that are not dates stay listed under it.
// `sort` puts the two sort orders at the top of the menu, worded for the column's kind as
// Excel's filter button words them; `format` is the DISPLAY text of an option (the value
// filtered on is unchanged). Both optional, so every other grid keeps its menu as it was.
export default function HeaderFilter({ active, open, optionsFor, selected, onToggleOpen, onChange, dateColumn = false, sort, format }: {
  active: boolean;
  open: boolean;
  optionsFor: () => string[];
  selected: string[];
  onToggleOpen: () => void;
  onChange: (v: string[]) => void;
  dateColumn?: boolean;
  sort?: { kind: SortKind; dir: SortDir | null; onSort: (dir: SortDir) => void };
  format?: (v: string) => string;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) { setQuery(""); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      setPos({
        top: Math.min(r.bottom + 4, window.innerHeight - 320),
        left: Math.min(r.left, window.innerWidth - 280),
      });
    }
    const onDown = (e: MouseEvent) => {
      if (popRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      onToggleOpen();
    };
    const onScroll = (e: Event) => {
      // Scrolling INSIDE the popover (its own options list) must not close it.
      if (popRef.current && e.target instanceof Node && popRef.current.contains(e.target)) return;
      onToggleOpen();
    };
    // Esc closes and returns focus to the trigger — without this the popover can only
    // be dismissed with the mouse, and a keyboard user is stranded inside it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onToggleOpen();
      btnRef.current?.focus();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options = open ? optionsFor() : [];
  const q = query.trim().toLowerCase();
  const tree = open && dateColumn ? buildDateTree(options) : null;
  const useTree = tree !== null && tree.years.length > 0;
  // Under the tree only the non-date values are listed flat.
  const flat = useTree ? tree.other : options;
  const matches = q
    ? flat.filter((o) => o.toLowerCase().includes(q) || (format ? format(o).toLowerCase().includes(q) : false))
    : flat;
  const shown = matches.slice(0, MAX_OPTIONS);

  const toggle = (o: string) => onChange(selected.includes(o) ? selected.filter((s) => s !== o) : [...selected, o]);

  return (
    <>
      <button ref={btnRef} className={`th-filter ${active ? "th-filter-active" : ""}`}
        title={active ? "Filter active" : "Filter"} onClick={onToggleOpen}
        aria-expanded={open} aria-haspopup="dialog"
        aria-label={active ? "Filter (active)" : "Filter"}>
        {active ? "●" : "▾"}
      </button>
      {open && pos && createPortal(
        <div ref={popRef} className="filter-popover" role="dialog" aria-label="Column filter"
          style={{ top: pos.top, left: pos.left }}>
          {sort && (
            <div className="filter-sort" role="group" aria-label="Sort">
              {(sort.kind === "text" ? ["asc", "desc"] as const : ["desc", "asc"] as const).map((d) => (
                <button key={d} type="button" aria-pressed={sort.dir === d}
                  className={"filter-sort-opt" + (sort.dir === d ? " is-active" : "")}
                  onClick={() => { sort.onSort(d); onToggleOpen(); }}>
                  Sort {SORT_LABELS[sort.kind][d]}
                </button>
              ))}
            </div>
          )}
          <input className="input" style={{ margin: 6, width: "calc(100% - 12px)", padding: "5px 8px", fontSize: "0.8rem" }}
            placeholder="Type to narrow…" value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
          {selected.length > 0 && (
            <button className="btn" style={{ margin: "0 6px 6px", fontSize: "0.75rem", padding: "3px 8px" }} onClick={() => onChange([])}>
              Clear selection
            </button>
          )}
          <div style={{ maxHeight: useTree ? 300 : 220, overflowY: "auto" }}>
            {useTree && (
              <DateTreeList tree={filterDateTree(tree, q)} selected={selected} onChange={onChange}
                expandAll={q !== ""} />
            )}
            {useTree && shown.length > 0 && (
              <div className="muted" style={{ padding: "6px 10px 2px", fontSize: "0.75rem" }}>Other values</div>
            )}
            {!useTree && shown.length === 0 && <div className="muted" style={{ padding: 8 }}>No values</div>}
            {shown.map((o) => (
              <label key={o} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 10px", cursor: "pointer", fontSize: "0.82rem" }}>
                <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{format ? format(o) : o}</span>
              </label>
            ))}
            {matches.length > MAX_OPTIONS && (
              <div className="muted" style={{ padding: "4px 10px" }}>
                Showing {MAX_OPTIONS.toLocaleString()} of {matches.length.toLocaleString()} — type to narrow.
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// A tri-state checkbox: ticked, blank, or the indeterminate dash for a partly-selected node.
function TriCheck({ state, onToggle, label }: { state: "all" | "some" | "none"; onToggle: () => void; label: string }) {
  return (
    <input type="checkbox" aria-label={label} checked={state === "all"} onChange={onToggle}
      ref={(el) => { if (el) el.indeterminate = state === "some"; }} />
  );
}

const ROW = { display: "flex", alignItems: "center", gap: 6, padding: "3px 10px", cursor: "pointer", fontSize: "0.82rem" } as const;
const TWISTY = { width: 14, border: "none", background: "none", padding: 0, cursor: "pointer", color: "inherit", fontSize: "0.7rem" } as const;

function DateTreeList({ tree, selected, onChange, expandAll }: {
  tree: DateTree; selected: string[]; onChange: (v: string[]) => void; expandAll: boolean;
}) {
  // Collapsed by default, like Excel; a search expands everything it kept.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const sel = new Set(selected);
  const isOpen = (k: string) => expandAll || expanded.has(k);
  const flip = (k: string) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  const twisty = (k: string, name: string) => (
    <button style={TWISTY} onClick={() => flip(k)} aria-expanded={isOpen(k)}
      aria-label={`${isOpen(k) ? "Collapse" : "Expand"} ${name}`}>{isOpen(k) ? "▾" : "▸"}</button>
  );
  if (tree.years.length === 0) return <div className="muted" style={{ padding: 8 }}>No matching dates</div>;
  return (
    <div role="tree">
      {tree.years.map((y) => (
        <div key={y.key} role="treeitem" aria-expanded={isOpen(y.key)}>
          <div style={ROW}>
            {twisty(y.key, y.label)}
            <TriCheck state={nodeState(y.values, sel)} label={y.label}
              onToggle={() => onChange(toggleNode(y.values, selected))} />
            <span onClick={() => flip(y.key)}>{y.label}</span>
          </div>
          {isOpen(y.key) && y.months.map((m) => (
            <div key={m.key} role="treeitem" aria-expanded={isOpen(m.key)}>
              <div style={{ ...ROW, paddingLeft: 28 }}>
                {twisty(m.key, `${m.label} ${y.label}`)}
                <TriCheck state={nodeState(m.values, sel)} label={`${m.label} ${y.label}`}
                  onToggle={() => onChange(toggleNode(m.values, selected))} />
                <span onClick={() => flip(m.key)}>{m.label}</span>
              </div>
              {isOpen(m.key) && m.days.map((d) => (
                <label key={d.key} role="treeitem" style={{ ...ROW, paddingLeft: 66 }}>
                  <TriCheck state={nodeState(d.values, sel)} label={d.label}
                    onToggle={() => onChange(toggleNode(d.values, selected))} />
                  <span>{d.label}</span>
                </label>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
